import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import debounce from 'lodash.debounce';
import InfiniteScroll from 'react-infinite-scroll-component';
import {
    BuildCircleOutlined,
    CarRepairOutlined,
    Close as CloseIcon,
    ControlPoint as ControlPointIcon,
    LocationOn,
    Sort as SortIcon,
} from '@mui/icons-material';
import { MenuItem as MuiMenuItem, Select, Tooltip, useTheme } from '@mui/material';
import Box from '@/components/ui/Box/Box';
import Button from '@/components/ui/Button/Button';
import IconButton from '@/components/ui/IconButton/IconButton';
import SearchInput from '@/components/ui/SearchInput/SearchInput';
import Typography from '@/components/ui/Typography/Typography';
import DateRangePicker from '@/components/business/DateRangePicker/DateRangePicker';
import FilterDropdown from '@/components/business/FilterDropdown/FilterDropdown';
import Loading from '@/components/business/Loading/Loading';
import Page from '@/components/business/Page/Page';
import { catalogApi, type CatalogEntry } from '@/api/catalog';
import { useCompany } from '@/providers/CompanyProvider/CompanyProvider';
import { useJobs } from '@/providers/JobsProvider/JobsProvider';
import { useLocations } from '@/providers/LocationsProvider/LocationsProvider';
import { useRepairers } from '@/providers/RepairersProvider/RepairersProvider';
import type { FilterOption } from '@/types/types';
import type { JobsFiltersState } from '@/providers/JobsProvider/JobsProvider';
import JobCard from './components/JobCard/JobCard';
import JobDetails from './components/JobDetails/JobDetails';
import { styles } from './styles';

type QuickFilterId = 'All Jobs' | 'Open' | 'Closed';

const quickFilters: QuickFilterId[] = ['All Jobs', 'Open', 'Closed'];

const quickFilterStatuses: Record<QuickFilterId, string[] | null> = {
    'All Jobs': null,
    Open: ['Open', 'Accepted', 'In Progress', 'Declined'],
    Closed: ['Completed', 'Closed', 'Canceled'],
};

const subFiltersForQuick: Record<QuickFilterId, string[]> = {
    'All Jobs': [],
    Open: ['Open', 'Accepted', 'In Progress'],
    Closed: ['Completed', 'Canceled'],
};

const subFilterToStatuses: Record<string, string[]> = {
    Open: ['Open'],
    Accepted: ['Accepted'],
    'In Progress': ['In Progress'],
    Declined: ['Declined'],
    Completed: ['Completed', 'Closed'],
    Canceled: ['Canceled'],
};

const JOB_LIST_SCROLLABLE_ID = 'job-list-scrollable';

const JobsPage = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const jobs = useJobs();
    const locationsCtx = useLocations();
    const repairersCtx = useRepairers();
    const companyCtx = useCompany();
    const [selectedQuickFilter, setSelectedQuickFilter] = useState<QuickFilterId>('All Jobs');
    const [selectedSubFilter, setSelectedSubFilter] = useState<string | null>(null);
    const [localSearch, setLocalSearch] = useState('');
    const [catalog, setCatalog] = useState<CatalogEntry[]>([]);

    const isRepairer = companyCtx.state.company?.marketplace_side === 'Repairer';

    const SEARCH_MIN_LEN = 3;
    const SEARCH_DEBOUNCE_MS = 300;
    const pushSearch = useRef(
        debounce((value: string) => {
            jobs.actions.setSearchTerm(value);
        }, SEARCH_DEBOUNCE_MS),
    ).current;

    useEffect(() => () => pushSearch.cancel(), [pushSearch]);

    const handleSearchChange = (value: string) => {
        setLocalSearch(value);
        if (value.length === 0 || value.length >= SEARCH_MIN_LEN) {
            pushSearch(value);
        }
    };

    useEffect(() => {
        void locationsCtx.actions.refreshLocations();
        void repairersCtx.actions.refreshRepairers();
        let cancelled = false;
        void (async () => {
            try {
                const res = await catalogApi.list();
                if (!cancelled) setCatalog(res.data?.payload ?? []);
            } catch {}
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        const statuses = selectedSubFilter
            ? subFilterToStatuses[selectedSubFilter]
            : quickFilterStatuses[selectedQuickFilter];
        jobs.actions.setQuickFilters(
            'service_statuses',
            statuses && statuses.length ? statuses.join(',') : null,
        );
    }, [selectedQuickFilter, selectedSubFilter]);

    const locationOptions = useMemo<FilterOption[]>(
        () =>
            locationsCtx.state.locations.map((loc) => ({
                value: loc.id,
                name:
                    loc.nickname ??
                    (`${loc.city ?? ''} ${loc.state ?? ''}`.trim() || 'Location'),
                selected:
                    jobs.state.filters.locations.find((f) => f.value === loc.id)?.selected ??
                    false,
            })),
        [locationsCtx.state.locations, jobs.state.filters.locations],
    );

    const repairerOptions = useMemo<FilterOption[]>(
        () =>
            repairersCtx.state.repairers.map((r) => ({
                value: r.id,
                name: r.name,
                selected:
                    jobs.state.filters.repairers.find((f) => f.value === r.id)?.selected ?? false,
            })),
        [repairersCtx.state.repairers, jobs.state.filters.repairers],
    );

    const serviceTypeOptions = useMemo<FilterOption[]>(() => {
        const names = Array.from(
            new Set(catalog.map((c) => c.service_type).filter(Boolean)),
        ) as string[];
        return names.map((name) => ({
            value: name,
            name,
            selected:
                jobs.state.filters.service_types.find((f) => f.value === name)?.selected ?? false,
        }));
    }, [catalog, jobs.state.filters.service_types]);

    const filterMap = useMemo(() => {
        const result: Record<string, string[]> = {};
        (Object.entries(jobs.state.filters) as [string, FilterOption[]][]).forEach(
            ([key, options]) => {
                const selected = options
                    .filter((o) => o.selected)
                    .map((o) => o.value);
                if (selected.length) result[key] = selected;
            },
        );
        return result;
    }, [jobs.state.filters]);

    const dependencyKey = useMemo(
        () =>
            JSON.stringify({
                filterMap,
                quickFilters: jobs.state.quickFilters,
                searchTerm: jobs.state.searchTerm,
                sort: jobs.state.sort,
                dateFilter: jobs.state.dateFilter,
            }),
        [
            filterMap,
            jobs.state.quickFilters,
            jobs.state.searchTerm,
            jobs.state.sort,
            jobs.state.dateFilter,
        ],
    );

    useEffect(() => {
        void jobs.actions.loadJobs({
            searchTerm: jobs.state.searchTerm,
            sort: jobs.state.sort,
            filters: filterMap,
            quickFilters: jobs.state.quickFilters,
            dateFilter: jobs.state.dateFilter,
        });
    }, [dependencyKey]);

    const activeJob = jobs.state.jobs.find((j) => j.id === jobs.state.activeJob);

    const handleFilterChange = (
        key: keyof JobsFiltersState,
        sourceOptions: FilterOption[],
        selectedValues: string[],
    ) => {
        const next = sourceOptions.map((opt) => ({
            ...opt,
            selected: selectedValues.includes(opt.value),
        }));
        jobs.actions.setFilters({ [key]: next });
    };

    const hasActiveFilters =
        Boolean(localSearch) ||
        Boolean(jobs.state.searchTerm) ||
        Boolean(jobs.state.dateFilter.start) ||
        Boolean(jobs.state.dateFilter.end) ||
        (Object.values(jobs.state.filters) as FilterOption[][]).some((opts) =>
            opts.some((o) => o.selected),
        );

    const handleClearAll = () => {
        pushSearch.cancel();
        setLocalSearch('');
        jobs.actions.setSearchTerm('');
        jobs.actions.setDateFilter({ start: null, end: null });
        jobs.actions.setFilters({
            locations: jobs.state.filters.locations.map((o) => ({ ...o, selected: false })),
            repairers: jobs.state.filters.repairers.map((o) => ({ ...o, selected: false })),
            service_types: jobs.state.filters.service_types.map((o) => ({
                ...o,
                selected: false,
            })),
        });
    };

    const subFilters = subFiltersForQuick[selectedQuickFilter];

    return (
        <Page>
            <Box sx={styles.titleAndActions}>
                <Typography variant="h1">Jobs</Typography>
                <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<ControlPointIcon />}
                    sx={styles.newJobButton}
                    onClick={() => navigate('/new_job')}
                >
                    New Job
                </Button>
            </Box>
            <Box sx={styles.quickFilterRow}>
                {quickFilters.map((filter, idx) => (
                    <Button
                        key={filter}
                        sx={styles.quickFilterBtn(selectedQuickFilter === filter, theme, idx)}
                        onClick={() => {
                            setSelectedQuickFilter(filter);
                            setSelectedSubFilter(null);
                        }}
                    >
                        {filter}
                    </Button>
                ))}
            </Box>
            <Box sx={styles.contentContainer(theme)}>
                {subFilters.length > 0 && (
                    <Box sx={styles.subFilterRow}>
                        {subFilters.map((sub) => (
                            <Button
                                key={sub}
                                sx={styles.subFilterBtn(selectedSubFilter === sub, theme)}
                                onClick={() =>
                                    setSelectedSubFilter(selectedSubFilter === sub ? null : sub)
                                }
                            >
                                {sub}
                            </Button>
                        ))}
                    </Box>
                )}
                <Box sx={styles.filterRow}>
                    <Box sx={styles.filterLeft}>
                        <SearchInput
                            value={localSearch}
                            onChange={handleSearchChange}
                            placeholder="Quick find"
                        />
                        <Select
                            size="small"
                            value={jobs.state.sort}
                            onChange={(e) =>
                                jobs.actions.setSort(e.target.value as 'asc' | 'desc')
                            }
                            sx={styles.sortSelect(theme)}
                        >
                            <MuiMenuItem value="desc" sx={{ fontSize: '12px' }}>
                                <SortIcon
                                    sx={{
                                        fontSize: 16,
                                        mr: 1,
                                        transform: 'scaleY(1)',
                                    }}
                                />
                                Newest
                            </MuiMenuItem>
                            <MuiMenuItem value="asc" sx={{ fontSize: '12px' }}>
                                <SortIcon
                                    sx={{
                                        fontSize: 16,
                                        mr: 1,
                                        transform: 'scaleY(-1)',
                                    }}
                                />
                                Oldest
                            </MuiMenuItem>
                        </Select>
                    </Box>
                    <Box sx={styles.filterRight}>
                        <DateRangePicker
                            start={jobs.state.dateFilter.start}
                            end={jobs.state.dateFilter.end}
                            disableFuture
                            onDateRangeChanged={(start, end) =>
                                jobs.actions.setDateFilter({ start, end })
                            }
                        />
                        <FilterDropdown
                            title="Locations"
                            options={locationOptions}
                            icon={LocationOn}
                            onChange={(values) =>
                                handleFilterChange('locations', locationOptions, values)
                            }
                        />
                        <FilterDropdown
                            title="Repairers"
                            options={repairerOptions}
                            icon={BuildCircleOutlined}
                            onChange={(values) =>
                                handleFilterChange('repairers', repairerOptions, values)
                            }
                        />
                        <FilterDropdown
                            title="Service"
                            options={serviceTypeOptions}
                            icon={CarRepairOutlined}
                            onChange={(values) =>
                                handleFilterChange('service_types', serviceTypeOptions, values)
                            }
                        />
                        <Tooltip title={hasActiveFilters ? 'Clear all filters' : ''}>
                            <span>
                                <IconButton
                                    size="small"
                                    disabled={!hasActiveFilters}
                                    onClick={handleClearAll}
                                    sx={styles.clearAllBtn(theme)}
                                    aria-label="Clear all filters"
                                >
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>
                </Box>
                <Box sx={styles.layoutRow}>
                    <Box
                        id={JOB_LIST_SCROLLABLE_ID}
                        sx={styles.jobListPanel(theme, Boolean(activeJob))}
                    >
                        {jobs.state.loading ? (
                            <Loading
                                loading
                                fullScreen={false}
                                width={120}
                                height={120}
                                timeoutMessage="Could not load jobs."
                            />
                        ) : jobs.state.jobs.length === 0 ? (
                            <Box sx={styles.emptyState}>
                                <Typography>No jobs found.</Typography>
                            </Box>
                        ) : (
                            <InfiniteScroll
                                dataLength={jobs.state.jobs.length}
                                next={() => {
                                    if (jobs.state.paging?.next_page) {
                                        void jobs.actions.loadMoreJobs(
                                            jobs.state.paging.next_page,
                                        );
                                    }
                                }}
                                hasMore={Boolean(jobs.state.paging?.next_page)}
                                scrollableTarget={JOB_LIST_SCROLLABLE_ID}
                                loader={
                                    <Loading
                                        loading
                                        fullScreen={false}
                                        width={100}
                                        height={100}
                                        timeoutMessage="Could not load jobs."
                                    />
                                }
                                endMessage={
                                    <Typography sx={styles.endMessage}>All jobs loaded</Typography>
                                }
                            >
                                {jobs.state.jobs.map((job) => (
                                    <JobCard
                                        key={job.id}
                                        job={job}
                                        isRepairer={isRepairer}
                                        isActive={job.id === jobs.state.activeJob}
                                        onClick={jobs.actions.setActiveJob}
                                    />
                                ))}
                            </InfiniteScroll>
                        )}
                    </Box>
                    <Box sx={styles.jobDetailsPanel(!activeJob)}>
                        {activeJob ? (
                            <>
                                <Box
                                    sx={styles.mobileBackBar(theme)}
                                    onClick={() => jobs.actions.setActiveJob(null)}
                                >
                                    ← Back to jobs
                                </Box>
                                <JobDetails job={activeJob} isRepairer={isRepairer} />
                            </>
                        ) : (
                            <Box sx={styles.emptyState}>
                                <Typography>Select a job to see details.</Typography>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>
        </Page>
    );
};

export default JobsPage;
